import sortBy from 'lodash.sortby';
import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import uniq from 'lodash.uniq';
import { OutputParametricSelector } from 'reselect';

import { AppState } from 'services/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import {
    SprayEntityIds,
    SprayApplicationRate
} from 'services/sprayEvents/types';
import { selectEntities } from 'services/entities/selectors';
import { selectSprayEventTypeIsWeed } from 'services/sprayEventType/selectors';
import { NozzleType } from 'services/equipmentSets/constants';
import { EntityRowSpacing } from 'services/entities/types';
import {
    EquipmentSet,
    EquipmentSetsState,
    EquipmentSetsProps,
    EquipmentSetId,
    CalibrationId,
    Calibration,
    CalibrationProps
} from './types';

const selectEquipmentSets = (state: AppState): EquipmentSetsState =>
    state.equipmentSets;

const selectEquipmentSetId = (
    _state: AppState,
    props: EquipmentSetsProps
): EquipmentSetId | undefined => props.equipmentSetId;
const selectCalibrationId = (
    _state: AppState,
    props: EquipmentSetsProps
): CalibrationId | undefined => props.calibrationId;
const selectEntityIds = (
    _state: AppState,
    props: EquipmentSetsProps
): SprayEntityIds => props.entityIds;
const selectApplicationRate = (
    _state: AppState,
    props: EquipmentSetsProps
): SprayApplicationRate => props.applicationRate;

const selectEquipmentSetsKeyedByUuid = createDeepEqualSelector(
    selectEquipmentSets,
    (equipmentSets) => keyBy(equipmentSets, 'uuid')
);

const selectEquipmentSetsArray = createDeepEqualSelector(
    [selectEquipmentSets],
    (equipmentSets) => Object.values(equipmentSets)
);

const selectEquipmentSetsKeyedById = createDeepEqualSelector(
    selectEquipmentSetsArray,
    (equipmentSets) => keyBy(equipmentSets, 'id')
);

const selectEquipmentSet = createDeepEqualSelector(
    [selectEquipmentSets, selectEquipmentSetId],
    (equipmentSets, equipmentSetId) =>
        (equipmentSetId && get(equipmentSets, equipmentSetId, [])) || []
);

const makeSelectEquipmentSetField = (field: string): any =>
    createDeepEqualSelector(selectEquipmentSet, (equipmentSet) =>
        get(equipmentSet, field)
    );

const selectCalibrations = createDeepEqualSelector(
    selectEquipmentSet,
    (equipmentSet) => get(equipmentSet, 'calibrations', [])
);

const selectEntitiesRowSpacing = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    (entityIds, entities) => {
        const rowSpacings: any[] = [];

        entityIds &&
            entityIds.forEach((entityId) => {
                if (
                    entityId &&
                    entities[entityId] &&
                    entities[entityId].rowSpacing
                ) {
                    rowSpacings.push(entities[entityId].rowSpacing);
                }
            });

        return sortBy(uniq(rowSpacings));
    }
);

const makeSelectEntitiesRowSpacing = (): typeof selectEntitiesRowSpacing =>
    selectEntitiesRowSpacing;

const selectCalibrationsKeyedById = createDeepEqualSelector(
    selectCalibrations,
    (calibrations) => keyBy(calibrations, 'id')
);

const selectCalibrationsSortedByRowSpacingMin = createDeepEqualSelector(
    selectCalibrations,
    (calibrations) => sortBy(calibrations, 'rowSpacingMin')
);

const makeSelectMissingCalibrations = (): any =>
    createDeepEqualSelector(
        selectCalibrationsSortedByRowSpacingMin,
        selectEntitiesRowSpacing,
        (calibrations, entitiesRowSpacing) =>
            entitiesRowSpacing.filter((rowSpacing) => {
                if (!rowSpacing) return false;

                return !calibrations.some(
                    ({ rowSpacingMin, rowSpacingMax }) =>
                        rowSpacing >= rowSpacingMin &&
                        rowSpacing <= rowSpacingMax
                );
            })
    );

const selectCalibrationsObject = createDeepEqualSelector(
    selectCalibrationsSortedByRowSpacingMin,
    selectEntitiesRowSpacing,
    selectApplicationRate,
    (calibrations, entitiesRowSpacing, applicationRateState) => {
        const rowSpacingsWithoutCalibrations: EntityRowSpacing[] = [];
        const rowSpacingsWithCorrectSetup: EntityRowSpacing[] = [];
        const rowSpacingsWithInvalidRate: EntityRowSpacing[] = [];

        entitiesRowSpacing.forEach((rowSpacing) => {
            if (!rowSpacing) return;

            const filteredCalibrations = calibrations.filter(
                ({ rowSpacingMin, rowSpacingMax }) =>
                    rowSpacing >= rowSpacingMin && rowSpacing <= rowSpacingMax
            );

            if (!filteredCalibrations.length) {
                rowSpacingsWithoutCalibrations.push(rowSpacing);
                return;
            }

            const calibrationHasCorrectRate = filteredCalibrations.some(
                ({ applicationRate }) =>
                    applicationRate === applicationRateState
            );

            if (!calibrationHasCorrectRate) {
                rowSpacingsWithInvalidRate.push(rowSpacing);
                return;
            }

            rowSpacingsWithCorrectSetup.push(rowSpacing);
        });

        return {
            rowSpacingsWithoutCalibrations,
            rowSpacingsWithCorrectSetup,
            rowSpacingsWithInvalidRate
        };
    }
);

const selectRowSpacingsWithoutCalibrations = createDeepEqualSelector(
    selectCalibrationsObject,
    (calibrationsObject) =>
        get(calibrationsObject, 'rowSpacingsWithoutCalibrations', [])
);

const makeSelectRowSpacingsWithoutCalibrations =
    (): typeof selectRowSpacingsWithoutCalibrations =>
        selectRowSpacingsWithoutCalibrations;

const selectRowSpacingsWithCorrectSetup = createDeepEqualSelector(
    selectCalibrationsObject,
    (calibrationsObject) =>
        get(calibrationsObject, 'rowSpacingsWithCorrectSetup', [])
);

const makeSelectRowSpacingsWithCorrectSetup =
    (): typeof selectRowSpacingsWithCorrectSetup =>
        selectRowSpacingsWithCorrectSetup;

const selectRowSpacingsWithInvalidRate = createDeepEqualSelector(
    selectCalibrationsObject,
    (calibrationsObject) =>
        get(calibrationsObject, 'rowSpacingsWithInvalidRate', [])
);

const makeSelectRowSpacingsWithInvalidRate =
    (): typeof selectRowSpacingsWithInvalidRate =>
        selectRowSpacingsWithInvalidRate;

const makeSelectCalibrations = (): any =>
    createDeepEqualSelector(
        selectCalibrationsSortedByRowSpacingMin,
        selectEntitiesRowSpacing,
        selectApplicationRate,
        (calibrations, entitiesRowSpacing, applicationRateState) =>
            calibrations.filter(
                ({ rowSpacingMin, rowSpacingMax, applicationRate }) => {
                    if (
                        Number(applicationRateState) !== Number(applicationRate)
                    ) {
                        return false;
                    }

                    return entitiesRowSpacing.some((rowSpacing) => {
                        if (rowSpacing) {
                            return (
                                rowSpacing >= rowSpacingMin &&
                                rowSpacing <= rowSpacingMax
                            );
                        }

                        return false;
                    });
                }
            )
    );

const makeSelectCalibration = (): any =>
    createDeepEqualSelector(
        selectCalibrationsKeyedById,
        selectCalibrationId,
        (calibrations, calibrationId) =>
            calibrationId && get(calibrations, calibrationId)
    );

const selectCalibrationRowSpacingMin = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'rowSpacingMin')
);

const makeSelectCalibrationRowSpacingMin =
    (): typeof selectCalibrationRowSpacingMin => selectCalibrationRowSpacingMin;

const selectCalibrationRowSpacingMax = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'rowSpacingMax')
);

const makeSelectCalibrationRowSpacingMax =
    (): typeof selectCalibrationRowSpacingMax => selectCalibrationRowSpacingMax;

const selectCalibrationGear = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'gear')
);

const makeSelectCalibrationGear = (): typeof selectCalibrationGear =>
    selectCalibrationGear;

const selectCalibrationSpeed = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'speed')
);

const makeSelectCalibrationSpeed = (): typeof selectCalibrationSpeed =>
    selectCalibrationSpeed;

const selectCalibrationFlowRate = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'flowRate')
);

const makeSelectCalibrationFlowRate = (): typeof selectCalibrationFlowRate =>
    selectCalibrationFlowRate;

const selectCalibrationGaugePressure = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'gaugePressure')
);

const makeSelectCalibrationGaugePressure =
    (): typeof selectCalibrationGaugePressure => selectCalibrationGaugePressure;

const selectCalibrationEngineRpm = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'engineRpm')
);

const makeSelectCalibrationEngineRpm = (): typeof selectCalibrationEngineRpm =>
    selectCalibrationEngineRpm;

const selectCalibrationFanSpeed = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'fanSpeed')
);

const makeSelectCalibrationFanSpeed = (): typeof selectCalibrationFanSpeed =>
    selectCalibrationFanSpeed;

const selectCalibrationSwathWidth = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'swathWidth')
);

const makeSelectCalibrationSwathWidth =
    (): typeof selectCalibrationSwathWidth => selectCalibrationSwathWidth;

const selectCalibrationNozzlesPerSide = createDeepEqualSelector(
    makeSelectCalibration(),
    (calibration) => get(calibration, 'nozzlesPerSide')
);

const makeSelectCalibrationNozzlesPerSide =
    (): typeof selectCalibrationNozzlesPerSide =>
        selectCalibrationNozzlesPerSide;

const makeSelectCalibrationValue = <T extends keyof Calibration>(
    fieldName: T
): OutputParametricSelector<
    AppState,
    CalibrationProps,
    Calibration[T],
    (
        arg: ReturnType<ReturnType<typeof makeSelectCalibration>>
    ) => Calibration[T]
> =>
    createDeepEqualSelector(makeSelectCalibration(), (calibration) =>
        get(calibration, fieldName)
    );

const selectEquipmentSetsArrayBySprayEventType = createDeepEqualSelector(
    [selectEquipmentSetsArray, selectSprayEventTypeIsWeed],
    (equipmentSetsArray, isWeedSpray) => {
        const filterFn = isWeedSpray
            ? ({ nozzleType }: EquipmentSet) =>
                  nozzleType === NozzleType.WEED_BOOM
            : ({ nozzleType }: EquipmentSet) =>
                  [NozzleType.HV, NozzleType.LV].includes(nozzleType);

        return equipmentSetsArray.filter(filterFn);
    }
);

const selectEquipmentSetOptionsBySprayEventType = createDeepEqualSelector(
    [selectEquipmentSetsArrayBySprayEventType],
    (equipmentSets) =>
        sortBy(equipmentSets, 'name').map(({ id, name }) => ({
            key: id,
            text: name,
            value: id
        }))
);

const selectUniqueApplicationRates = createDeepEqualSelector(
    selectEquipmentSetsArrayBySprayEventType,
    (equipmentSetsArray) => {
        const uniqueApplicationRates = new Set<number>();

        if (equipmentSetsArray) {
            equipmentSetsArray.forEach((item: EquipmentSet) => {
                const { calibrations } = item;
                calibrations.forEach(({ applicationRate }) =>
                    uniqueApplicationRates.add(applicationRate)
                );
            });
        }

        return Array.from(uniqueApplicationRates).sort((a, b) => a - b);
    }
);

const selectApplicationRateOptions = createDeepEqualSelector(
    selectUniqueApplicationRates,
    (applicationRates) => {
        const result: {
            key: number;
            text: string;
            value: number;
        }[] = [];

        applicationRates.forEach((applicationRate) => {
            result.push({
                key: applicationRate,
                text: applicationRate.toString(),
                value: applicationRate
            });
        });

        return result;
    }
);

export {
    selectEquipmentSets,
    selectEquipmentSetsKeyedByUuid,
    selectEquipmentSetsArray,
    selectEquipmentSetsKeyedById,
    selectEquipmentSet,
    selectCalibrations,
    selectCalibrationsKeyedById,
    selectEquipmentSetId,
    selectCalibrationId,
    makeSelectMissingCalibrations,
    makeSelectCalibrations,
    makeSelectCalibration,
    makeSelectEntitiesRowSpacing,
    makeSelectEquipmentSetField,
    selectCalibrationsObject,
    makeSelectRowSpacingsWithoutCalibrations,
    makeSelectRowSpacingsWithCorrectSetup,
    makeSelectRowSpacingsWithInvalidRate,
    makeSelectCalibrationRowSpacingMax,
    makeSelectCalibrationRowSpacingMin,
    makeSelectCalibrationGear,
    makeSelectCalibrationSpeed,
    makeSelectCalibrationFlowRate,
    makeSelectCalibrationGaugePressure,
    makeSelectCalibrationEngineRpm,
    makeSelectCalibrationFanSpeed,
    makeSelectCalibrationSwathWidth,
    makeSelectCalibrationNozzlesPerSide,
    makeSelectCalibrationValue,
    selectEquipmentSetsArrayBySprayEventType,
    selectEquipmentSetOptionsBySprayEventType,
    selectUniqueApplicationRates,
    selectApplicationRateOptions
};
